import { ButtonDropdown, Flex, NavListItem, NavListItemProps, Spacing } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUserOrganizations } from '@modules-deprecated/app/organizations'
import { useUmbrella, useUserUmbrellas } from '@modules-deprecated/app/umbrellas'
import { isSettingsModulePath } from '@views/settings/utils/isSettingsModulePath'
import { routeToInternalSettings } from '@views/settings/utils/routeToInternalSettings'
import { routeToUmbrella } from '@views/umbrella/utils/routeToUmbrella'

import { EmberUserType, useEmberUserType } from '../../../../contexts/emberUserTypeContext'
import { EmberRoute } from '../../../../enums/emberRoute'
import { Scope } from '../../../../enums/scope'
import { useEmberRouteToUrlGenerator } from '../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { useAdvisorAccess } from '../../../../hooks/useAdvisorAccess'
import { useOrganizationUiSetup } from '../../../../hooks/useOrganizationUiSetup'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { isEmberRoute } from '../../../../utils/routing/isEmberRoute'
import { useNavigation } from '../../contexts/navigationContext'
import { BackToAdvisorButton } from '../BackToAdvisorButton'
import { ButtonContent } from './elements/ButtonContent'
import { DropdownHeader } from './elements/DropdownHeader'
import { LogoutListItem } from './elements/LogoutListItem'
import { OrganizationDropdownIcon } from './elements/OrganizationDropdownIcon'
import { OrganizationDropdownSkeleton } from './elements/OrganizationDropdownSkeleton'
import { getItems } from './utils/getItems'
import { getOrganizationsItems } from './utils/getOrganizationsItems'

export const OrganizationDropdown = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { umbrella } = useUmbrella()
  const { organization } = useUserOrganization()
  const { userOrganizations } = useUserOrganizations()
  const { userUmbrellas } = useUserUmbrellas()
  const { generateUrl } = useEmberRouteToUrlGenerator()
  const { userType } = useEmberUserType()
  const { isInverted, shouldCloseDropdown, variant } = useNavigation()
  const { shouldShowBackButton, umbrellaUrlPath } = useAdvisorAccess()

  const ownerUserTypes: EmberUserType[] = ['Accountant', 'Business Owner']
  const userIsOrganizationOwner = ownerUserTypes.includes(userType)
  const { isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()
  const isLoading = !organization && !umbrella
  const selectedId = umbrella?.id || organization?.id
  const shouldShowUserSettings = !isLoading && !organization?.isLocked
  const shouldShowSettings = !isLoading && !organization?.isLocked && !umbrella
  const shouldShowSubscription =
    !isUiSetupSupernavInvoicingAccounting &&
    isAuthorized(Scope.OrganizationSubscriptionWrite, false, userIsOrganizationOwner) &&
    !organization?.isLocked

  const { items } = getItems({
    t,
    isUmbrella: Boolean(umbrella),
    shouldShowUserSettings,
    shouldShowSettings,
    shouldShowSubscription,
  })
  const { organizationsItems } = getOrganizationsItems({ userOrganizations, userUmbrellas })

  const handleSelect = useCallback(
    (_: string, value: EmberRoute | string) => {
      const isInSettingsModule = isSettingsModulePath()

      if (value === EmberRoute.SettingsOrganization && isInSettingsModule) {
        routeToInternalSettings(EmberRoute.SettingsOrganization)
        return
      }

      if (value === EmberRoute.SettingsUser && isInSettingsModule) {
        routeToInternalSettings(EmberRoute.SettingsUser)
        return
      }

      if (value === EmberRoute.SettingsSubscription && isInSettingsModule) {
        routeToInternalSettings(EmberRoute.SettingsSubscription)
        return
      }

      if (value === EmberRoute.SettingsUserUmbrella) {
        routeToUmbrella(EmberRoute.SettingsUserUmbrella)
        return
      }

      if (generateUrl && isEmberRoute(value)) {
        const url = generateUrl(value)
        navigate(url)
      }
    },
    [generateUrl, navigate],
  )

  const handleRenderDropdownItem = useCallback(
    (props: NavListItemProps<string>) => {
      const itemVariant = isInverted ? 'inverted' : props.item.variant
      const item = { ...props.item, variant: itemVariant }

      if (props.item.id === 'logout') {
        return <LogoutListItem {...props} item={item} />
      }

      return <NavListItem {...props} item={item} />
    },
    [isInverted],
  )

  if (isLoading) {
    return <OrganizationDropdownSkeleton />
  }

  return (
    <Flex alignItems="center" gap={Spacing.S4}>
      {shouldShowBackButton && umbrellaUrlPath && <BackToAdvisorButton umbrellaUrlPath={umbrellaUrlPath} />}
      <ButtonDropdown
        dropdownItemRender={(props) => handleRenderDropdownItem(props)}
        items={items}
        variant={isInverted ? 'textOnColor' : 'text'}
        dropdownVariant={variant}
        dropdownHeader={<DropdownHeader organizationsItems={organizationsItems} selectedId={selectedId} />}
        icon={<OrganizationDropdownIcon shouldShowBackButton={shouldShowBackButton} variant={variant} />}
        selectedId={selectedId}
        dropdownMaxHeight="fullScreen"
        size="lg"
        shouldClose={shouldCloseDropdown}
        onSelect={handleSelect}
        iconRight="chevronsUpDown"
        fullWidth
      >
        <ButtonContent variant={variant} />
      </ButtonDropdown>
    </Flex>
  )
}
